import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material"
import { CorrelogramScatter } from "@features/results/Result/CorrelogramScatter"
import { NodeToNodeDensity } from "@components/NodeToNodeDensity"
import { useGetRunsNodesResultsHistoricQuery } from "@store/services/runService"
import React, { useEffect, useMemo, useRef, useState } from "react"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import {
  getResultsDataType,
  getResultsTimesteps,
  getUnitFormats,
} from "@features/results/Result/DrawerComparable/helpers"
import { calculateLagShift } from "@utils/run"
import { convertHistoricToRunResults } from "@features/results/Result/runResult.utils"

type DrawerComparableProps = {
  runId: string
  selectedNodes: string[]
  onClose: () => void
  version?: string
}

export const DrawerComparable = ({
  runId,
  selectedNodes,
  onClose,
  version,
}: DrawerComparableProps) => {
  const [selectedTimesteps, setSelectedTimesteps] = useState<(number | string)[]>([])

  const gridRef = useRef<HTMLDivElement | null>(null)
  const correlationGridRef = useRef<HTMLDivElement | null>(null)

  const { isFetching, data: historicResponse } = useGetRunsNodesResultsHistoricQuery({
    runKeys: [runId],
    nodes: selectedNodes.map(graphViewGroupNodeLabelToSampleNode),
  })
  const nodesResults = useMemo(
    () => convertHistoricToRunResults(historicResponse?.results),
    [historicResponse?.results],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  const datasets = nodesResults?.[runId] ?? {}

  const lagShift = useMemo(
    () => calculateLagShift(historicResponse?.results),
    [historicResponse?.results],
  )
  const unitFormats = useMemo(() => getUnitFormats(selectedNodes), [selectedNodes])
  const timesteps = useMemo(() => getResultsTimesteps(datasets, lagShift), [datasets, lagShift])
  const dataType = useMemo(() => getResultsDataType(datasets), [datasets])

  useEffect(() => {
    if (timesteps.length === 0) return

    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    setSelectedTimesteps([timesteps[0]])
  }, [timesteps])

  return (
    <Box sx={{ m: 2 }}>
      <Button
        onClick={onClose}
        variant="contained"
        sx={{
          width: "100px",
          position: "fixed",
          top: "12px",
          right: "6vw",
        }}
      >
        Close
      </Button>
      {dataType === "string" ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <Typography sx={{ fontWeight: 700, p: 2 }} variant="h5">
            The selected nodes have incompatible data type.
          </Typography>
        </Box>
      ) : (
        <Grid container={true} sx={{ mt: 2, alignItems: "end" }} ref={gridRef}>
          <Grid item={true} xs={3}>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="timestep-label">Timesteps</InputLabel>
              <Select
                labelId="timestep-label"
                id="timestep-select"
                value={selectedTimesteps}
                label="Timesteps"
                onChange={(value: SelectChangeEvent<(number | string)[]>) =>
                  setSelectedTimesteps(
                    Array.isArray(value.target.value) ? value.target.value : [value.target.value],
                  )
                }
                size={"small"}
              >
                <MenuItem value={""}>&nbsp;</MenuItem>
                {timesteps?.map((item) => {
                  return (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <DownloadChartImageDialog
              buttonProps={{ disabled: isFetching, sx: { ml: 1 } }}
              chartRef={correlationGridRef}
              defaultFileName="Correlation Chart"
              runReference={runId}
            />
          </Grid>
          {isFetching ? (
            <Grid item xs={12} sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
                <CircularProgress />
              </Box>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} sx={{ mb: 2 }}>
                <CorrelogramScatter
                  datasets={datasets}
                  formats={unitFormats}
                  selectedTimesteps={selectedTimesteps}
                  ref={correlationGridRef}
                  version={version}
                />
              </Grid>
              <Grid item xs={12} sx={{ mb: 2 }}>
                <NodeToNodeDensity
                  datasets={datasets}
                  formats={unitFormats}
                  selectedTimesteps={selectedTimesteps}
                  width={(gridRef?.current?.clientWidth || 1200) - 60}
                  height={Math.round(((gridRef?.current?.clientWidth || 1200) - 60) / 2)}
                  version={version}
                  runId={runId}
                />
              </Grid>
            </>
          )}
        </Grid>
      )}
    </Box>
  )
}
